import React from "react";
import { Link } from "react-router-dom";
import type { Product } from "../../hooks/products/types";

interface PromotionBase {
  title: string;
  subtitle: string;
  image: string;
  bg: string;
  link?: string;
}
interface PromotionOne extends PromotionBase {
  features?: string[];
  price?: string;
}
interface PromotionTwo extends PromotionBase {
  button?: string;
  text?: string;
}
interface PromotionThree extends PromotionBase {
  button?: string;
}

interface PromotionsProps {
  variant?: "one" | "two" | "three";
  products?: Product[];
}

const promotionsData: {
  one: PromotionOne[];
  two: PromotionTwo[];
  three: PromotionThree[];
} = {
  one: [
    {
      title: "World's Best RO Water Purifiers",
      subtitle: "Water 100% Pure",
      features: [
        "Makes Water 100% Pure",
        "Multiple Purification Process",
        "Retains Essential Minerals",
      ],
      image:
        "https://media.istockphoto.com/id/1441225869/photo/reverse-osmosis-water-purification-system-under-sink-in-a-kitchen-water-cleaning-system.jpg?s=1024x1024&w=is&k=20&c=5p4nrYx1Iq1cTqIhImKzAG8pUAdWuruSwjcw_lthe4U=",
      bg: "bg-blue-100",
    },
    {
      title: "Smart Kitchen Appliances",
      subtitle: "Cook Smart, Cook Tasty and Cook Easy",
      image:
        "https://media.istockphoto.com/id/2156679156/photo/set-of-home-kitchen-appliances-in-the-room-with-sun-light.jpg?s=1024x1024&w=is&k=20&c=VISz0EilXB38cjIUmwWPmLxaNyAHLWo2DXGhRzIqSBc=",
      bg: "bg-red-100",
    },
    {
      title: "Electric Kettles",
      subtitle: "High capacity electric kettles for boiling water",
      price: "$82",
      image:
        "https://www.shutterstock.com/shutterstock/photos/2369452549/display_1500/stock-photo-electric-kettle-plugged-into-power-socket-on-dark-grey-wall-indoors-2369452549.jpg",
      bg: "bg-green-100",
    },
  ],
  two: [
    {
      title: "kitchen appliances",
      subtitle:
        "Keep cabinets, countertops & more in order with the right solves. Get it now!",
      button: "Shop Now",
      image:
        "https://images.unsplash.com/photo-1484154218962-a197022b5858?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&q=80&w=1174",
      bg: "bg-[#1a2940]",
      text: "text-orange-400",
    },
  ],
  three: [
    {
      title: "Make Everyday A Fine Dining Experience",
      subtitle: "With Our Kitchen Appliances",
      button: "Shop Now",
      image:
        "https://www.shutterstock.com/shutterstock/photos/2663001653/display_1500/stock-photo-a-beautifully-arranged-dining-table-with-glasses-and-plates-overlooking-the-water-perfect-for-a-2663001653.jpg",
      bg: "bg-blue-400",
    },
    {
      title: "Be the best gift giver this Wedding Season!",
      subtitle: "With Our Kitchen Appliances",
      button: "Shop Now",
      image: "/assets/Images/kitchen-appliances.png",
      bg: "bg-teal-400",
    },
  ],
};

const Promotions: React.FC<PromotionsProps> = ({
  variant = "one",
  products,
}) => {
  // If products are provided, dynamic mapping overrides hardcoded data
  const data = products
    ? products.map((p, i) => ({
        title: p.name,
        subtitle: p.description
          ? p.description.slice(0, 60) + "..."
          : "Check out this amazing product!",
        image: p.image,
        bg: ["bg-blue-100", "bg-red-100", "bg-green-100"][i % 3], // Cycle backgrounds
        price: p.price ? `$${p.price.toFixed(2)}` : undefined,
        button: "Shop Now",
        link: `/product/${p.id}`,
      }))
    : promotionsData[variant];

  // For variant two, we only need one item, so take the first one if dynamic
  const displayData =
    products && variant === "two"
      ? [data[0]]
      : products && variant === "three"
        ? data.slice(0, 2)
        : data;

  if (!displayData || displayData.length === 0) return null;

  if (variant === "one") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-7xl px-4 mx-auto">
        {displayData.map((promo, idx) => (
          <div
            key={idx}
            className="relative rounded-xl overflow-hidden min-h-70 flex items-center justify-center shadow-lg group"
            style={{
              backgroundImage: `url(${promo.image})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="absolute inset-0 bg-linear-to-br from-black/60 via-black/40 to-black/10 transition-opacity group-hover:opacity-90" />
            <div className="relative z-10 flex flex-col items-center justify-center text-center w-full px-8 py-10">
              <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-2 drop-shadow-lg line-clamp-2">
                {promo.title}
              </h2>
              <div className="text-lg md:text-xl text-white/90 mb-2 font-medium drop-shadow line-clamp-2">
                {promo.subtitle}
              </div>
              {"features" in promo && promo.features && (
                <ul className="list-disc list-inside mb-4 text-white/80 text-sm">
                  {promo.features.map((f: string, i: number) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
              )}
              {"price" in promo && promo.price && (
                <div className="text-base text-white/80 mb-2">
                  Starting from{" "}
                  <span className="font-bold text-white">{promo.price}</span>
                </div>
              )}
              <Link
                to={promo.link || "/products"}
                className="mt-4 bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-2 rounded-full shadow-lg transition"
              >
                Shop Now
              </Link>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (variant === "two") {
    const promo = displayData[0];
    if (!promo) return null;
    return (
      <div
        className="relative rounded-xl overflow-hidden min-h-70 flex items-center justify-center shadow-lg max-w-7xl mx-auto px-4 group"
        style={{
          backgroundImage: `url(${promo.image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-linear-to-br from-black/70 via-black/40 to-black/10 transition-opacity group-hover:opacity-90" />
        <div className="relative z-10 flex flex-col items-center justify-center text-center w-full px-8 py-10">
          <h2 className="text-3xl md:text-4xl font-extrabold text-orange-400 mb-2 drop-shadow-lg line-clamp-2">
            {promo.title}
          </h2>
          <div className="text-lg md:text-xl text-white/90 mb-4 font-medium drop-shadow line-clamp-2">
            {promo.subtitle}
          </div>
          <Link
            to={promo.link || "/products"}
            className="mt-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-2 rounded-full shadow-lg transition"
          >
            {("button" in promo && promo.button) || "Shop Now"}
          </Link>
        </div>
      </div>
    );
  }

  if (variant === "three") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-4 max-w-7xl mx-auto">
        {displayData.map((promo, idx) => (
          <div
            key={idx}
            className="relative rounded-xl overflow-hidden min-h-55 flex items-center justify-center shadow-lg group"
            style={{
              backgroundImage: `url(${promo.image})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="absolute inset-0 bg-linear-to-br from-black/60 via-black/40 to-black/10 transition-opacity group-hover:opacity-90" />
            <div className="relative z-10 flex flex-col items-center justify-center text-center w-full px-8 py-10">
              <h2 className="text-xl md:text-2xl font-extrabold text-white mb-2 drop-shadow-lg line-clamp-2">
                {promo.title}
              </h2>
              <div className="text-lg md:text-xl text-white/90 mb-4 font-medium drop-shadow line-clamp-2">
                {promo.subtitle}
              </div>
              <Link
                to={promo.link || "/products"}
                className="mt-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-2 rounded-full shadow-lg transition"
              >
                {("button" in promo && promo.button) || "Shop Now"}
              </Link>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

export default Promotions;
